import React from "react";
import { useFormContext } from "react-hook-form";

import { AutoFormFieldProps } from "@autoform/react";
import _ from "lodash";
import { Plus, Trash } from "lucide-react";

import { BadgeSelector } from "@/components/fields/badge-selector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export type TagListFieldCustomData = {
  options?: Array<{ value: string; label: string }>;
};

/**
 * Generic string[] field with optional badge shortcuts.
 * Register globally as `tagList` fieldType.
 *
 * Schema usage:
 *   myField.superRefine(fieldConfig({ fieldType: "tagList" }))
 *   myField.superRefine(fieldConfig({ fieldType: "tagList", customData: { options: [...] } }))
 */
export const TagListField: React.FC<AutoFormFieldProps> = ({
  id,
  label,
  field,
  inputProps,
}) => {
  const form = useFormContext();
  const value: string[] = form.watch(id) ?? [];
  const options = (
    field.fieldConfig?.customData as TagListFieldCustomData | undefined
  )?.options;

  const setValue = (newValues: string[]) => {
    form.setValue(id, newValues, { shouldDirty: true });
    inputProps.onChange({
      target: { name: id, value: newValues },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  // Badge options are {value, label} → convert to {id, name} for BadgeSelector
  const badgeItems =
    options?.map((o) => ({ id: o.value, name: o.label })) ?? [];
  // Current value as {id, name} for BadgeSelector comparison
  const badgeValues = value.map((v) => ({ id: v, name: v }));
  const onBadgeChange = (items: Array<{ id: string; name: string }>) => {
    setValue(items.map((i) => i.id));
  };

  return (
    <div className="flex flex-col space-y-2 text-foreground w-full">
      {badgeItems.length > 0 && (
        <div className="flex flex-row space-x-2 space-y-2 flex-wrap">
          {badgeItems.map((item) => (
            <BadgeSelector
              key={item.id}
              item={item}
              values={badgeValues}
              onChange={onBadgeChange}
            />
          ))}
        </div>
      )}
      {value.map((item: string, index: number) => (
        <div key={index} className="flex flex-row space-x-2 w-full items-end">
          <Input
            className="text-foreground"
            defaultValue={item}
            onBlur={(e) => {
              const newValues = [...value];
              newValues[index] = e.target.value;
              setValue(newValues);
            }}
          />
          <Button
            variant="secondary"
            size="icon"
            type="button"
            className="hover:bg-zinc-300 hover:text-black focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 dark:bg-white dark:text-black dark:hover:bg-zinc-300 dark:hover:text-black"
            onClick={() => setValue(value.filter((_, i) => i !== index))}
          >
            <Trash className="size-4" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="secondary"
        onClick={() => setValue([...value, ""])}
        className="mt-4 flex items-center text-foreground"
      >
        <Plus className="mr-2" size={16} />
        Add
      </Button>
    </div>
  );
};

export default TagListField;
